"use client";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { Button, Checkbox, Form, Input, message } from "antd/lib";
import type { IRegistrationPortalPayload } from "@/models/registrationPortal";
import { CategoryType, PurposeType, RegistrationPortalEnum } from "@/models/registrationPortal";
import useMyMutation from "@/hooks/userMyMutation";
import { useEffect } from "react";
import { Select, Typography } from "antd";

// Convert enums to select options
const purposeOptions = Object.values(PurposeType).map(value => ({
  label: value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" "),
  value: value,
}));

const categoryOptions = Object.values(CategoryType).map(value => ({
  label: value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" "),
  value: value,
}));

interface FormValues {
  type: RegistrationPortalEnum;
  companyName: string;
  description?: string;
  website: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  telegram: string;
  agreement: boolean;
  location: string;
  twitter: string;
  linkedin?: string;
  message?: string;
  category?: CategoryType;
  purpose?: PurposeType;
}

const defaultValues: FormValues = {
  type: RegistrationPortalEnum.klarda_partner,
  companyName: "",
  description: "",
  website: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  position: "",
  telegram: "",
  agreement: false,
  location: "",
  twitter: "",
};

const RegistrationForm = () => {
  const t = useTranslations("registration_portal");
  const [form] = Form.useForm<FormValues>();
  const values = Form.useWatch([], form);
  const searchParams = useSearchParams();
  const type = searchParams.get("type");
  useEffect(() => {
    if (type && Object.values(RegistrationPortalEnum).includes(type as RegistrationPortalEnum)) {
      form.setFieldsValue({ type: type as RegistrationPortalEnum });
    } else {
      form.setFieldsValue({ type: RegistrationPortalEnum.klarda_partner });
    }
  }, [type, form]);

  const { mutate: triggerLikeComment } = useMyMutation(
    {
      url: `/v1/user/registration_portal`,
      method: "post",
    },
    {
      onSuccess() {
        message.success(t("Registration_submitted_successfully"));
        form.resetFields();
      },
      onError(error) {
        message.error(t("Failed_to_submit_registration") + ": " + error.message);
      },
    },
  );

  const onFinish = (values: FormValues) => {
    const payload: IRegistrationPortalPayload = {
      description: values.description || "",
      type: values.type || RegistrationPortalEnum.klarda_partner,
      website: values.website,
      email: values.email,
      position: values.position,
      phone: values.phone,
      telegram: values.telegram,
      first_name: values.firstName,
      last_name: values.lastName,
      country: values.location,
      name: values.companyName,
      message: values.message,
      twitter: values.twitter,
      linkedin: values.linkedin,
      category: values.category,
      purpose: values.purpose,
    };
    triggerLikeComment(payload);
  };

  return (
    <div className="mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-3xl sm:text-4xl md:text-[48px] font-semibold text-center mb-4">{t("Registration_Portal")}</h1>
      <p className="text-center text-gray-500 text-lg sm:text-xl md:text-2xl">{t("description")}</p>

      <Form
        form={form}
        name="registration"
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
        initialValues={defaultValues}
        className="max-w-[768px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 space-x-0 space-y-0 mt-8 md:mt-14"
        size="large"
      >
        {/* <Form.Item
          className="col-span-1 md:col-span-2"
          name="type"
          rules={[{ required: true, message: t("Please_select_a_type") }]}
        >
          <div className="flex flex-wrap justify-center gap-4 my-6 sm:my-8 md:my-[30px]">
            {Object.values(RegistrationPortalEnum).map(value => (
              <Button
                key={value}
                type={values?.type === value ? "primary" : "default"}
                size="large"
                onClick={() => {
                  form.setFieldValue("type", value);
                  form.validateFields(["type"]);
                }}
              >
                {t(value)}
              </Button>
            ))}
          </div>
        </Form.Item> */}

        <Form.Item
          name="firstName"
          label={
            <span className="font-medium">
              {t("Your_First_name")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_enter_your_first_name") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="lastName"
          label={
            <span className="font-medium">
              {t("Your_Last_name")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_enter_your_last_name") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label={
            <span className="font-medium">
              {t("Your_Email")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[
            { required: true, message: t("Please_enter_your_email") },
            { type: "email", message: t("Please_enter_a_valid_email") },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="phone"
          label={<span className="font-medium">{t("Your_Phone")}</span>}
          rules={[
            {
              pattern: /^\d+$/,
              message: t("Please_enter_valid_phone_number"),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="position"
          label={
            <span className="font-medium">
              {t("Your_Position")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_enter_your_position") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="telegram"
          label={
            <span className="font-medium">
              {t("Your_Telegram")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_enter_your_telegram") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="category"
          label={
            <span className="font-medium">
              {t("Select_category")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_select_category") }]}
          className="col-span-1 md:col-span-2"
        >
          <Select className="w-full" options={categoryOptions} />
        </Form.Item>

        <Form.Item
          name="companyName"
          label={
            <span className="font-medium">
              {t("Your_Project_Company_name")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_enter_your_project_company_name") }]}
          className="col-span-1 md:col-span-2"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={<span className="font-medium">{t("Project_Company_Description")}</span>}
          className="col-span-1 md:col-span-2"
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="website"
          label={
            <span className="font-medium">
              {t("Your_Project_Company_website")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[
            { required: true, message: t("Please_enter_your_website") },
            { type: "url", message: t("Please_enter_a_valid_URL") },
          ]}
          className="col-span-1 md:col-span-2"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="location"
          label={
            <span className="font-medium">
              {t("Where_is_Your_Project_Company_based")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_enter_your_location") }]}
          className="col-span-1 md:col-span-2"
        >
          <Input />
        </Form.Item>

        <Typography.Text className="col-span-1 md:col-span-2">{t("Social")}</Typography.Text>
        <Form.Item name="twitter" label={<span className="font-medium">X</span>}>
          <Input />
        </Form.Item>
        <Form.Item name="linkedin" label={<span className="font-medium">Linkedin</span>}>
          <Input />
        </Form.Item>
        <Form.Item
          name="purpose"
          required
          label={
            <span className="font-medium">
              {t("Select_purpose")}
              <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: t("Please_select_purpose") }]}
          className="col-span-1 md:col-span-2"
        >
          <Select className="w-full" options={purposeOptions} />
        </Form.Item>
        <Form.Item
          name="message"
          label={<span className="font-medium">{t("Message")}</span>}
          className="col-span-1 md:col-span-2"
        >
          <Input.TextArea rows={4} />
        </Form.Item>
        <Form.Item
          name="agreement"
          valuePropName="checked"
          className="flex justify-center col-span-1 md:col-span-2"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error(t("Please_agree_to_terms"))),
            },
          ]}
        >
          <Checkbox>
            <span className="text-[12px] text-gray-500">
              {t("By_submitting_this_form")}
              <a href="https://inside.klarda.com/terms-and-conditions" className="text-black underline font-medium">
                {t("Terms_of_Service")}
              </a>{" "}
              {t("and")}{" "}
              <a href="https://inside.klarda.com/privacy-policy" className="text-black underline font-medium">
                {t("Privacy_Statement")}
              </a>
              .
            </span>
          </Checkbox>
        </Form.Item>
        <Form.Item className="col-span-1 md:col-span-2 flex justify-center">
          <Button type="primary" htmlType="submit" className="w-fit mx-auto">
            {t("Submit")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default RegistrationForm;
